// src/sections/HowItWorks.jsx
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { howItWorks } from "../data/siteContent";
import {
  cardReveal,
  staggerParent,
  viewportOnce,
  fadeUp,
} from "../utils/motion";

export default function HowItWorks() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const opacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [0.3, 1, 1, 0.3],
  );

  return (
    <section
      className="section-block how-it-works-section"
      id="how"
      ref={sectionRef}
    >
      {/* Decorative elements */}
      <div className="how-it-works__bg-decoration" />
      <div className="how-it-works__glow how-it-works__glow--one" />
      <div className="how-it-works__glow how-it-works__glow--two" />

      <div className="container-shell">
        <motion.div
          style={{ opacity }}
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <div className="section-intro section-intro--center">
            <div className="section-eyebrow">
              <span className="section-eyebrow__dot" />
              {howItWorks.eyebrow}
            </div>
            <h2 className="how-it-works__title">{howItWorks.title}</h2>
            <div className="section-intro__divider" />
            <p className="how-it-works__description">
              {howItWorks.description}
            </p>
          </div>
        </motion.div>

        <motion.div
          className="steps-grid"
          variants={staggerParent}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {howItWorks.steps.map((step, index) => (
            <motion.article
              key={step.number}
              className="step-card"
              variants={cardReveal}
              whileHover="hover"
              initial="initial"
              animate="animate"
            >
              <div className="step-card__connector">
                {index < howItWorks.steps.length - 1 && (
                  <div className="step-card__line" />
                )}
              </div>

              <div className="step-card__header">
                <div className="step-card__number-wrapper">
                  <div className="step-card__number-bg" />
                  <div className="step-card__number">{step.number}</div>
                </div>
                <div className="step-card__icon">
                  {index === 0 && (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M4 4H20V20H4V4Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 8H16V16H8V8Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="2" fill="currentColor" />
                    </svg>
                  )}
                  {index === 1 && (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 2L15 9H22L16 14L19 21L12 16.5L5 21L8 14L2 9H9L12 2Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="2" fill="currentColor" />
                    </svg>
                  )}
                  {index === 2 && (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 7V12L15 15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="17" cy="7" r="2" fill="currentColor" />
                    </svg>
                  )}
                  {index === 3 && (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 12V18H4V12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22 8L12 3L2 8L12 13L22 8Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 8V12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
              </div>

              <div className="step-card__content">
                <h3 className="step-card__title">{step.title}</h3>
                <p className="step-card__description">{step.desc}</p>
              </div>

              <div className="step-card__footer">
                <div className="step-card__progress">
                  <div
                    className="step-card__progress-bar"
                    style={{ width: `${(index + 1) * 25}%` }}
                  />
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div
          className="how-it-works__cta"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <button className="how-it-works__learn-more">
            <span>Learn more about our process</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12H19M19 12L12 5M19 12L12 19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </motion.div>
      </div>
    </section>
  );
}
